import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

interface InactivityWarningDialogProps {
  open: boolean;
  secondsRemaining: number;
  onStayLoggedIn: () => void;
}

const InactivityWarningDialog: React.FC<InactivityWarningDialogProps> = ({
  open,
  secondsRemaining,
  onStayLoggedIn,
}) => {
  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      // Prevent closing by clicking the backdrop
      onClose={(_event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          onStayLoggedIn();
        }
      }}
      PaperProps={{
        sx: { borderRadius: 2, textAlign: "center", p: 1 },
      }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <AccessTimeIcon color="warning" sx={{ fontSize: 40 }} />
          <Typography variant="h6" fontWeight={600}>
            Session Timeout Warning
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          You have been inactive for a while. For your security, you will be
          automatically logged out in:
        </Typography>

        <Box sx={{ position: "relative", display: "inline-flex", mb: 1 }}>
          <CircularProgress
            variant="determinate"
            value={(secondsRemaining / 60) * 100}
            size={80}
            thickness={4}
            color={secondsRemaining <= 10 ? "error" : "warning"}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: "absolute",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="h5"
              fontWeight={700}
              color={secondsRemaining <= 10 ? "error.main" : "warning.main"}
            >
              {secondsRemaining}
            </Typography>
          </Box>
        </Box>

        <Typography variant="caption" color="text.secondary" display="block">
          seconds remaining
        </Typography>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", pb: 2, gap: 1 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={onStayLoggedIn}
          size="large"
          sx={{ minWidth: 160 }}
        >
          Stay Logged In
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InactivityWarningDialog;
